import { PublicBusiness } from './businesses';
import { DateTimeISO, Money, PaginatedResponse } from './common';
import { PublicCompany } from './companies';
import { PublicTeammate } from './teammates';
import { PublicUser } from './user';

export enum ApplicationType {
  INDIVIDUAL = 0,
  JOINT = 1,
  BUSINESS = 2,
}

export type BankAccountListAggregateInfo = {
  totalBankAccounts: number;
  totalAvailableBalance: Money;
  totalTransactions: number;
  totalDeposits: number;
  totalWithdrawals: number;
};

export enum BankAccountStatus {
  Open,
  Closed,
  Locked,
}

export type LockReasonsFormValues = {
  uuid: string;
  lockReason: string | number;
};

export enum BankAccountLockReasons {
  BEING_INVESTIGATED = 0,
  INVESTIGATED_ACH = 1,
  INVESTIGATED_WIRE = 2,
  CHECK_ISSUED = 3,
  CHECK_DEPOSIT = 4,
  INVESTIGATED_CARD = 5,
}

export enum BankAccountListSortBy {
  CREATED_ASCENDING = 'created ascending',
  CREATED_DESCENDING = 'created descending',
  COMPANY_ASCENDING = 'company ascending',
  COMPANY_DESCENDING = 'company descending',
  OPENED_ASCENDING = 'opened ascending',
  OPENED_DESCENDING = 'opened descending',
  AVAILABLE_BALANCE_ASCENDING = 'available balance ascending',
  AVAILABLE_BALANCE_DESCENDING = 'available balance descending',
}

export type BankAccountOwnerProfile = {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string;
  fullName: string;
  email: string;
};

export type BankAccountOwner = {
  id: string;
  primary: boolean;
  profile: BankAccountOwnerProfile;
};

export type BankAccountListItem = {
  uuid: string;
  applicationType: ApplicationType;
  accountTypeStr: string;
  availableBalance: Money;
  currentBalance: Money;
  accountNumberLast4: string;
  companyName: string;
  advisorName: string;
  status: BankAccountStatus;
  callerIsAuthorizedTeammate: boolean;
  createdAt: DateTimeISO;
  dateOpening: DateTimeISO;
  primaryOwner: BankAccountOwner;
  totalOwners: number;
  businessName: string;
};

export type BankAccountsListResponse = {
  bankAccounts: PaginatedResponse<BankAccountListItem>;
  aggregateInfo: BankAccountListAggregateInfo;
};

export type BankAccountsListQueryParams = {
  page: number;
  account_statuses?: BankAccountStatus;
  teammate_id?: string;
  enhanced_fdic_insurance_status?: DepositSweepStatus;
  company_id?: string;
  consumer_id?: string;
  records_per_page?: number;
  sort_by?: BankAccountListSortBy;
};

export enum DepositSweepStatus {
  ENROLLED = 'Enrolled',
  NOT_ENROLLED = 'Not Enrolled',
  UNENROLLED = 'Unenrolled',
}

export type BankAccountDepositSweeps = {
  uuid: string;
  tpDepositSweepId: string;
  status: DepositSweepStatus;
  enrolledAt: DateTimeISO;
  enrolledBy: PublicUser;
  unenrolledAt?: DateTimeISO;
  unenrolledBy?: PublicUser;
};

export type BankAccountAggregateInfo = {
  totalStatements: number;
  totalTransactionRequests: number;
};

export type Fees = {
  incomingDomestic: Money;
  outgoingDomestic: Money;
  monthlySavingAccount: Money;
};

export type BankAccountDetails = {
  uuid: string;
  id: string;
  achCreditOverrideLimit: Money | null;
  achCreditOverrideLimitExpiredAt: DateTimeISO | null;
  achDebitOverrideLimit: Money | null;
  achDebitOverrideLimitExpiredAt: DateTimeISO | null;
  checkDepositOverrideLimit: Money | null;
  checkDepositOverrideLimitExpiredAt: DateTimeISO | null;
  checkIssuanceOverrideLimit: Money | null;
  checkIssuanceOverrideLimitExpiredAt: DateTimeISO | null;
  wireOutOverrideLimit: Money | null;
  wireOutOverrideLimitExpiredAt: DateTimeISO | null;
  applicationType: ApplicationType;
  accountTypeStr: string;
  availableBalance: Money;
  currentBalance: Money;
  accountNumber: string;
  routingNumber: string;
  savingsInterestRate: number | null;
  savingsInterestRateLastUpdatedAt: DateTimeISO | null;
  apy: number | null;
  accountNumberLast4: string;
  advisor: PublicTeammate;
  company: PublicCompany;
  business?: PublicBusiness;
  primaryOwner: BankAccountOwner;
  owners: BankAccountOwner[];
  status: BankAccountStatus;
  callerIsAuthorizedTeammate: boolean;
  createdAt: DateTimeISO;
  openedAt: DateTimeISO;
  depositSweep?: BankAccountDepositSweeps;
  lastDepositSweepEnrollmentAttempt: null;
  isWireOutOverrideEnabled: boolean;
  isCheckDepositOverrideEnabled: boolean;
  isCheckIssuanceOverrideEnabled: boolean;
  isAchCreditOverrideEnabled: boolean;
  isAchDebitOverrideEnabled: boolean;
  aggregateInfo: BankAccountAggregateInfo;
  fees: Fees;
};

type BankAccountEditFeesFields = {
  useCompanySetting: boolean;
  fee: string | number;
};

type BankAccountEditControlFields = {
  setLimit: boolean;
  limit: string | number;
  expiration: DateTimeISO;
};

export type BankAccountEditFormValues = {
  isStaff: boolean;
  uuid: string;
  advisor: string;
  incomingDomesticFee: BankAccountEditFeesFields;
  outgoingDomesticFee: BankAccountEditFeesFields;
  savingsAccountFee: BankAccountEditFeesFields;
  checkIssuingControls: BankAccountEditControlFields;
  wireControls: BankAccountEditControlFields;
  mobileCheckDepositControls: BankAccountEditControlFields;
  achCreditControls: BankAccountEditControlFields;
  achDebitControls: BankAccountEditControlFields;
};

export enum StatementType {
  MONTHLY = 'monthly',
  FORM1099 = '1099',
}

export type StatementItem = {
  bankAccountId: string;
  statementDate: DateTimeISO;
  statementType: StatementType;
  s3FilePath: string | null;
  year: number | null;
  presignedUrl: string | null;
};

export type StatementsResponse = PaginatedResponse<StatementItem>;

export enum TaxDocumentStatus {
  Processing = 'processing',
  PendingReview = 'pending_review',
  PendingFiling = 'pending_filing',
  Filed = 'filed',
  Deleted = 'deleted',
}

export type TaxDocumentItem = {
  uuid: string;
  bankAccountId: string;
  name: string;
  type: string;
  status: TaxDocumentStatus;
  year: string;
  url: string;
};

export type TaxDocumentsResponse = PaginatedResponse<TaxDocumentItem>;

export const queryKeys = {
  list: ['bankAccountsList'] as const,
  details: ['bankAccountsDetails'] as const,
  statements: ['bankAccountStatements'] as const,
  taxes: ['bankAccountTaxes'] as const,
  preflight: ['bankAccountPreflight'] as const,
};
